// src/components/modal/cart/CartStepsModal.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, MapPin, Truck, Tag } from "lucide-react";
import { useCreateOrder } from "../../../hooks/useCreateOrder";
import { useApi } from "../../../hooks/useApi";
import { COMPANY_KEY } from "../../../config/company";
import { useFreteCalculator } from "../../../hooks/useFreteCalculator";
import { useValidateCoupon } from "../../../hooks/useValidateCoupon";
import CouponInput from "./CouponInput";

// Helper pra formatar em R$
const formatBRL = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default function CartStepsModal({
  isOpen,
  onClose,
  cart,
  updateCartQuantity,
  removeFromCart,
  getTotalPrice,

  // entrega
  deliveryType,
  setDeliveryType,
  validarDelivery,

  // formulário cliente e endereço (mantido como já estava)
  customerInfo,
  setCustomerInfo,
  address,
  setAddress,
  maskCEP,
  onlyDigits,
  nameInvalid,
  phoneInvalid,
  cepInvalid,
  logradouroInvalid,
  bairroInvalid,
  ufInvalid,
  numeroInvalid,
  isFormValid,

  // pagamentos
  payments,
  paymentMethod,
  setPaymentMethod,
  paymentMethodSelected,
  setPaymentMethodSelected,

  // fluxo de pedido
  withBusy,
  sendToWhatsApp,
  handleOpenStatusModal,
  onOrderCreated, // Callback para notificar App.js quando pedido for criado
  onClearCart, // Callback para limpar carrinho após pedido criado
}) {
  const { createOrder, sendOnlyToWhatsApp } = useCreateOrder();
  const api = useApi();
  const {
    freight,
    loading: freightLoading,
    calculate: calculateFreight,
    clearFreight,
  } = useFreteCalculator();

  // Hook para validação de cupom
  const {
    loading: couponLoading,
    error: couponError,
    couponData,
    validateCoupon,
    clearCoupon,
    calculateDiscount,
  } = useValidateCoupon();

  const navigate = useNavigate();
  const [cartStep, setCartStep] = useState(1);
  const pixTextAreaRef = useRef(null);

  // PIX
  const [isPixModalOpen, setIsPixModalOpen] = useState(false);
  const [pixPayload, setPixPayload] = useState("");
  const [pixQrDataUrl, setPixQrDataUrl] = useState("");
  const [pixAmount, setPixAmount] = useState(0);
  const [pixTxid, setPixTxid] = useState("");
  const [loja, setLoja] = useState(false);
  const [pixCountdown, setPixCountdown] = useState(900); // 15min
  const [isGeneratingPix, setIsGeneratingPix] = useState(false);

  // Modal de mensagem
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info", // info, success, error, warning
  });

  const showMessage = (title, message, type = "info") => {
    setMessageModal({ isOpen: true, title, message, type });
  };

  const closeMessage = () => {
    setMessageModal({ isOpen: false, title: "", message: "", type: "info" });
  };

  const handleCopyPix = () => {
    try {
      // Navegadores modernos + https
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        navigator.clipboard.writeText(pixPayload || "").then(
          () => {
            // opcional: toast/alert
            // alert("Código Pix copiado!");
          },
          () => {
            // se der erro, tenta o fallback
            if (pixTextAreaRef.current) {
              pixTextAreaRef.current.select();
              document.execCommand("copy");
              // alert("Código Pix copiado!");
            }
          }
        );
      } else if (pixTextAreaRef.current) {
        // Fallback para ambientes sem navigator.clipboard (alguns iOS/Android)
        pixTextAreaRef.current.select();
        document.execCommand("copy");
        // alert("Código Pix copiado!");
      } else {
        // Último recurso
        // alert("Não foi possível copiar automaticamente. Selecione e copie o texto manualmente.");
      }
    } catch (e) {
      console.error("Erro ao copiar Pix:", e);
      // alert("Não foi possível copiar automaticamente. Selecione e copie o texto manualmente.");
    }
  };

  // Modal final de confirmação do pagamento
  const [isPaymentConfirmModalOpen, setIsPaymentConfirmModalOpen] =
    useState(false);

  // Constante do código da empresa

  // Reinicia steps ao abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setCartStep(1);
      setIsPixModalOpen(false);
      setIsPaymentConfirmModalOpen(false);
      setLoja(true); // Define como true por padrão
    } else {
      // Limpa frete ao fechar modal
      clearFreight();
    }
  }, [isOpen, clearFreight]);

  // Verifica disponibilidade da loja separadamente (apenas uma vez quando abre)
  useEffect(() => {
    const checkStoreAvailability = async () => {
      if (!isOpen) return;

      try {
        const data = await api.get(`/companies/${COMPANY_KEY}`);

        // Tenta diferentes estruturas de resposta
        let available = false;

        if (data?.data?.[0]?.available !== undefined) {
          available = data.data[0].available;
        } else if (data?.data?.available !== undefined) {
          available = data.data.available;
        } else if (data?.available !== undefined) {
          available = data.available;
        } else if (Array.isArray(data) && data[0]?.available !== undefined) {
          available = data[0].available;
        }

        setLoja(available);
      } catch (error) {
        console.error("❌ Erro ao verificar disponibilidade da loja:", error);
        setLoja(false);
      }
    };

    if (isOpen) {
      checkStoreAvailability();
    }
  }, [isOpen, api]);

  // contador do Pix
  useEffect(() => {
    if (!isPixModalOpen) return;
    setPixCountdown(15 * 60);
    const interval = setInterval(() => {
      setPixCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPixModalOpen]);

  const subtotalPrice = useMemo(() => getTotalPrice(), [getTotalPrice, cart]);

  // Calcula desconto do cupom
  const discountValue = useMemo(() => {
    return calculateDiscount(subtotalPrice);
  }, [calculateDiscount, subtotalPrice]);

  // Total com frete e desconto (apenas para delivery)
  const totalPrice = useMemo(() => {
    const subtotal = subtotalPrice;
    const freightValue =
      deliveryType === "delivery" && freight?.available ? freight.value : 0;
    const discount = discountValue;
    return Math.max(0, subtotal + freightValue - discount);
  }, [subtotalPrice, deliveryType, freight, discountValue]);

  const handleCloseAll = () => {
    setCartStep(1);
    setIsPixModalOpen(false);
    setIsPaymentConfirmModalOpen(false);
    onClose?.();
  };

  {
    /* modal para imagem do pix*/
  }
  const handleOpenPixModal = async (orderCode) => {
    console.log("[handleOpenPixModal] Recebido orderCode:", orderCode);
    const amount = Number(totalPrice || 0);

    try {
      const pixPayload = {
        amount: amount,
        description: `Pedido #${orderCode}`,
        name: customerInfo.name,
        contact: onlyDigits(customerInfo.phone),
        order_code: orderCode,
        pay_method: "pix",
      };

      console.log(
        "[handleOpenPixModal] Enviando payload para /pix/create:",
        pixPayload
      );

      const data = await api.post("/pix/create", pixPayload);

      console.log("[handleOpenPixModal] PIX GERADO:", JSON.stringify(data));

      const payload = data?.payload || "";
      const qrUrl = data?.qrcodeUrl || "";
      const txid = data?.txid || "";

      setPixAmount(amount);
      setPixPayload(payload);
      setPixQrDataUrl(qrUrl);
      setPixTxid(txid);
      setIsPixModalOpen(true);
    } catch (e) {
      console.error(
        "[handleOpenPixModal] Erro ao gerar QRCode Pix pela API:",
        e
      );
      showMessage(
        "Erro ao gerar PIX",
        "Não foi possível gerar o QR Code Pix. Tente novamente.",
        "error"
      );
    }
  };

  // Envia pedido para o backend
  const handleSendOrder = async () => {
    // Passa objeto completo do frete (contém value e calculationId)
    const freightData =
      deliveryType === "delivery" && freight?.available ? freight : null;

    // Obtém o código do cupom se houver
    const couponCode = couponData?.valid ? couponData.code : null;

    const result = await createOrder(
      customerInfo.name,
      onlyDigits(customerInfo.phone),
      deliveryType,
      address,
      paymentMethod,
      cart,
      getTotalPrice,
      freightData, // Passa objeto completo do frete
      couponCode // Passa código do cupom
    );

    // Salva no localStorage
    if (result && result.codigo) {
      const codigo = String(result.codigo);
      localStorage.setItem("pedido.codigo", codigo);
      localStorage.setItem("pedido.status", "0");
      localStorage.setItem("pedido.delivery", "0");

      // Salva dados do pedido para recuperar ao enviar WhatsApp
      const pedidoData = {
        codigo,
        customerInfo: {
          name: customerInfo.name,
          phone: onlyDigits(customerInfo.phone),
        },
        deliveryType,
        address,
        cart: JSON.parse(JSON.stringify(cart)), // Deep copy
        totalPrice: getTotalPrice(),
        paymentMethodSelected,
        freightData:
          deliveryType === "delivery" && freight?.available ? freight : null,
      };
      localStorage.setItem("pedido.data", JSON.stringify(pedidoData));

      // Notifica o App.js para atualizar os estados
      if (onOrderCreated) {
        onOrderCreated(codigo, address);
      }

      // Limpa carrinho e formulário
      if (onClearCart) {
        onClearCart();
      }

      console.log("✅ Pedido criado com código:", codigo);
    }

    return result;
  };

  // Validação dos dados do cliente
  const validateCustomerData = () => {
    const phoneDigits = onlyDigits(customerInfo.phone);

    if (!customerInfo.name || customerInfo.name.trim().length <= 3) {
      alert("Por favor, informe um nome com mais de 3 caracteres.");
      return false;
    }

    if (phoneDigits.length < 11) {
      alert(
        "Por favor, informe um número de celular válido. Ex: (11) 99999-9999"
      );
      return false;
    }

    // Se for delivery, validar endereço
    if (deliveryType === "delivery") {
      if (!address.cep || address.cep.length < 8) {
        alert("Por favor, informe um CEP válido para entrega.");
        return false;
      }
      if (!address.logradouro || !address.bairro || !address.estado) {
        alert("Por favor, preencha o endereço de entrega completo.");
        return false;
      }
      if (!address.numero || address.numero.trim() === "") {
        alert("Por favor, informe o número do endereço.");
        return false;
      }
      // Complemento é opcional, não precisa validar
      // Validar se o frete foi calculado para delivery
      if (!freight || !freight.available) {
        alert("Por favor, calcule o frete antes de finalizar o pedido.");
        return false;
      }
    }

    return true;
  };

  // Handler para finalizar pedido (PIX ou outras formas de pagamento)
  const handleFinalizeOrder = async () => {
    if (isGeneratingPix) return; // Previne múltiplos cliques

    // Valida dados do cliente
    if (!validateCustomerData()) {
      return;
    }

    setIsGeneratingPix(true);
    try {
      console.log("[handleFinalizeOrder] Iniciando criação do pedido...");

      // Cria o pedido primeiro
      const result = await handleSendOrder();

      console.log("[handleFinalizeOrder] Resultado da criação:", result);

      if (!result || !result.codigo) {
        console.error(
          "[handleFinalizeOrder] Código do pedido não encontrado:",
          result
        );
        throw new Error("Não foi possível obter o código do pedido.");
      }

      const orderCode = String(result.codigo);
      console.log("[handleFinalizeOrder] Código do pedido:", orderCode);

      // Verifica se é PIX
      const isPixPayment = (paymentMethodSelected || "")
        .toUpperCase()
        .includes("PIX");

      console.log(
        "[handleFinalizeOrder] Método de pagamento:",
        paymentMethodSelected
      );
      console.log("[handleFinalizeOrder] É PIX?", isPixPayment);

      if (isPixPayment) {
        // Se for PIX, abre modal do PIX com código do pedido
        console.log(
          "[handleFinalizeOrder] Abrindo modal PIX com código:",
          orderCode
        );
        await handleOpenPixModal(orderCode);
      } else {
        // Se não for PIX, abre modal de acompanhamento direto
        console.log(
          "[handleFinalizeOrder] Abrindo modal de acompanhamento (não-PIX)"
        );
        setIsPaymentConfirmModalOpen(true);
      }
    } catch (error) {
      console.error("[handleFinalizeOrder] Erro ao finalizar pedido:", error);
      showMessage(
        "Erro ao criar pedido",
        error.message || "Não foi possível criar o pedido. Tente novamente.",
        "error"
      );
    } finally {
      setIsGeneratingPix(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* MODAL PRINCIPAL (3 etapas) */}
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[9999]">
        <div className="bg-white rounded-2xl max-w-4xl w-[95vw] sm:w-[920px] max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {cartStep === 1 && "Carrinho de Compras"}
                {cartStep === 2 && "Entrega"}
                {cartStep === 3 && "Pagamento"}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Etapa {cartStep} de 3
              </p>
            </div>
            <button
              onClick={handleCloseAll}
              className="bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Conteúdo por etapa */}
          <div className="flex-1 overflow-y-auto">
            {cartStep === 1 && (
              <Step1Cart
                cart={cart}
                loja={loja}
                updateCartQuantity={updateCartQuantity}
                removeFromCart={removeFromCart}
                totalPrice={subtotalPrice}
                onClose={onClose}
                navigate={navigate}
                onNext={() => {
                  console.log("[CartStepsModal] Mudando para etapa 2");
                  setCartStep(2);
                }}
              />
            )}

            {cartStep === 2 && (
              <Step2Delivery
                deliveryType={deliveryType}
                setDeliveryType={setDeliveryType}
                validarDelivery={validarDelivery}
                showMessage={showMessage}
                customerInfo={customerInfo}
                setCustomerInfo={setCustomerInfo}
                address={address}
                setAddress={setAddress}
                maskCEP={maskCEP}
                onlyDigits={onlyDigits}
                nameInvalid={nameInvalid}
                phoneInvalid={phoneInvalid}
                cepInvalid={cepInvalid}
                logradouroInvalid={logradouroInvalid}
                bairroInvalid={bairroInvalid}
                ufInvalid={ufInvalid}
                numeroInvalid={numeroInvalid}
                subtotalPrice={subtotalPrice}
                freight={freight}
                freightLoading={freightLoading}
                calculateFreight={calculateFreight}
                clearFreight={clearFreight}
                totalPrice={totalPrice}
                isFormValid={isFormValid}
                onBack={() => setCartStep(1)}
                onNext={() => setCartStep(3)}
              />
            )}

            {cartStep === 3 && (
              <Step3Payment
                payments={payments}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                paymentMethodSelected={paymentMethodSelected}
                setPaymentMethodSelected={setPaymentMethodSelected}
                subtotalPrice={subtotalPrice}
                freight={freight}
                deliveryType={deliveryType}
                totalPrice={totalPrice}
                discountValue={discountValue}
                couponLoading={couponLoading}
                couponError={couponError}
                couponData={couponData}
                validateCoupon={validateCoupon}
                clearCoupon={clearCoupon}
                onBack={() => setCartStep(2)}
                onFinalizeOrder={handleFinalizeOrder}
                isFinalizing={isGeneratingPix}
              />
            )}
          </div>
        </div>
      </div>

      {/* MODAL PIX (QR Code + copia-e-cola + contador) */}
      {isPixModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Pagamento via Pix
                </h2>
                <p className="text-gray-600 text-sm">
                  Escaneie o QR Code ou use o código copia-e-cola para pagar.
                </p>
              </div>
              <button
                onClick={() => {
                  setIsPixModalOpen(false);
                  setIsPaymentConfirmModalOpen(true);
                }}
                className="bg-gray-100 hover:bg-gray-200 rounded-full p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col items-center gap-3">
              {pixQrDataUrl ? (
                <img
                  src={pixQrDataUrl}
                  alt="QR Code Pix"
                  className="w-60 h-60"
                />
              ) : (
                <div className="py-10 text-gray-500 text-sm">
                  Gerando QR Code...
                </div>
              )}
              <p className="text-lg font-semibold text-gray-800">
                Total: {formatBRL(pixAmount)}
              </p>
              <p
                className={`text-sm font-medium ${
                  pixCountdown > 0 ? "text-emerald-700" : "text-red-600"
                }`}
              >
                {pixCountdown > 0
                  ? `Tempo restante: ${formatCountdown(pixCountdown)}`
                  : "Tempo expirado. Gere um novo Pix se precisar."}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código Pix copia-e-cola
              </label>
              <textarea
                ref={pixTextAreaRef}
                readOnly
                defaultValue={pixPayload}
                className="w-full border border-gray-300 rounded-xl p-2 text-xs break-all bg-gray-50"
                rows={4}
              />

              <button
                type="button"
                onClick={handleCopyPix}
                className="mt-2 text-sm px-3 py-2 rounded-xl bg-gray-800 text-white hover:bg-gray-900"
              >
                Copiar código Pix
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsPixModalOpen(false);
                  setIsPaymentConfirmModalOpen(true);
                }}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition"
              >
                Já Paguei
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FINAL: Acompanhar / WhatsApp */}
      {isPaymentConfirmModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-gray-800">
              Pagamento confirmado?
            </h2>
            <p className="text-gray-600 text-sm">
              Após confirmar o pagamento, você pode acompanhar o pedido ou
              enviar os dados diretamente para o WhatsApp.
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  setIsPaymentConfirmModalOpen(false);
                  handleCloseAll();
                  handleOpenStatusModal?.();
                }}
                className="w-full px-4 py-2 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-primary-dark transition"
              >
                Acompanhar pedido
              </button>

              <button
                type="button"
                onClick={withBusy(async () => {
                  // Recupera dados do pedido salvos no localStorage
                  const pedidoDataStr = localStorage.getItem("pedido.data");

                  if (!pedidoDataStr) {
                    console.error(
                      "❌ Dados do pedido não encontrados no localStorage"
                    );
                    return;
                  }

                  const pedidoData = JSON.parse(pedidoDataStr);

                  // Envia para WhatsApp com os dados salvos
                  await sendOnlyToWhatsApp(
                    pedidoData.customerInfo.name,
                    pedidoData.customerInfo.phone,
                    pedidoData.deliveryType,
                    pedidoData.address,
                    pedidoData.cart,
                    () => pedidoData.totalPrice, // Função que retorna o total
                    pedidoData.paymentMethodSelected,
                    pedidoData.freightData
                  );

                  // Fecha modal de confirmação e modal do carrinho
                  setIsPaymentConfirmModalOpen(false);
                  handleCloseAll();

                  // Abre modal de acompanhamento do pedido
                  handleOpenStatusModal?.();
                })}
                className="w-full px-4 py-2 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition"
              >
                Enviar pedido no WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE MENSAGEM */}
      {messageModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10002] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {messageModal.type === "success" && (
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
                {messageModal.type === "error" && (
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                )}
                {messageModal.type === "warning" && (
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-amber-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                )}
                {messageModal.type === "info" && (
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {messageModal.title}
                </h3>
                <p className="text-gray-600 text-sm">{messageModal.message}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeMessage}
              className="w-full px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ====== Etapa 1 – Lista de produtos ====== */
function Step1Cart({
  cart,
  loja,
  updateCartQuantity,
  removeFromCart,
  totalPrice,
  onNext,
  onClose,
  navigate,
}) {
  const [isLoadingNext, setIsLoadingNext] = useState(false);

  const handleNext = async () => {
    if (isLoadingNext) return; // Previne múltiplos cliques

    console.log("[Step1Cart] Redirecionando para checkout...");
    setIsLoadingNext(true);
    try {
      // Fecha o modal
      onClose();
      // Redireciona para página de checkout
      navigate("/checkout");
    } catch (error) {
      console.error("[Step1Cart] Erro ao redirecionar:", error);
    } finally {
      setIsLoadingNext(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        Seu carrinho está vazio. 🛒
      </div>
    );
  }
  return (
    <div>
      <div className="p-6 space-y-4">
        {cart.map((item) => {
          const quantity = item.quantity || 1;
          const selectedIngredients = Array.isArray(item.selected_ingredients)
            ? item.selected_ingredients.filter((i) => i && i.name)
            : [];
          const selectedBaseIngredients = selectedIngredients.filter(
            (i) => !i.id && i.name
          );
          // Extras têm a flag isExtra: true
          const selectedExtraIngredients = selectedIngredients.filter(
            (i) => !i.id && i.name && i.isExtra === true
          );
          // Base ingredientes (sem extras) - não têm isExtra ou isExtra é false
          const selectedBaseIngredientsOnly = selectedIngredients.filter(
            (i) => !i.id && i.name && !i.isExtra
          );
          const selectedBaseProducts = selectedIngredients.filter(
            (i) => i.id && i.name
          );
          // Novo campo esperado no JSON do carrinho: `selected_produtos`
          // Pode ser um array de objetos { code, name, ingredients?: [] }
          // Se existir, priorizamos ele na exibição; caso contrário usamos o comportamento antigo
          const selectedProdutos =
            Array.isArray(item.selected_produtos) &&
            item.selected_produtos.length > 0
              ? item.selected_produtos
              : selectedBaseProducts;
          // Separa produtos base de extras produtos usando a flag isExtra
          const selectedBaseProdutosOnly = selectedProdutos.filter(
            (p) => !p.isExtra
          );
          const selectedExtraProdutos = selectedProdutos.filter(
            (p) => p.isExtra === true
          );
          const hasSelectedBaseProducts = selectedBaseProdutosOnly.length > 0;
          const hasExtraProducts = selectedExtraProdutos.length > 0;
          const hideBaseUnitPrice =
            hasSelectedBaseProducts && !hasExtraProducts;
          return (
            <div
              key={item.id}
              className="border border-gray-200 rounded-xl p-4 bg-white flex flex-col gap-2 shadow-sm"
            >
              <div className="flex items-center gap-3">
                {item.product_image && (
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1">
                  <p className="font-semibold text-gray-800 text-lg">
                    {item.product_name}
                  </p>
                  {!hideBaseUnitPrice && (
                    <p className="text-sm text-gray-500">
                      {formatBRL(item.product_price)} / un.
                    </p>
                  )}
                  {/* Produtos selecionados de combo (prioriza `selected_produtos` do item) */}
                  {selectedBaseProdutosOnly.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Produtos selecionados
                      </span>
                      <ul className="list-disc ml-5 mt-2 text-sm text-gray-700 space-y-1">
                        {selectedBaseProdutosOnly.map((prod, idx) => (
                          <li key={idx} className="mb-0">
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-gray-900">
                                {prod.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {prod.qty}x
                              </span>
                            </div>
                            {/* Se o produto selecionado também tiver ingredientes, mostra em sub-lista */}
                            {Array.isArray(prod.ingredients) &&
                              prod.ingredients.length > 0 && (
                                <ul className="list-disc ml-6 mt-1 text-sm text-gray-600">
                                  {prod.ingredients.map((ing, iidx) => (
                                    <li key={iidx}>{ing.name}</li>
                                  ))}
                                </ul>
                              )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Extra Produtos selecionados */}
                  {selectedExtraProdutos.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Extra Produtos
                      </span>
                      <ul className="list-disc ml-5 mt-2 text-sm text-gray-700 space-y-1">
                        {selectedExtraProdutos.map((prod, idx) => (
                          <li key={idx} className="mb-0">
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-gray-900">
                                {prod.name}
                              </span>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>x{prod.qty}</span>
                                {prod.price ? (
                                  <span>
                                    {formatBRL(prod.price * (prod.qty || 1))}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Ingredientes do produto principal */}
                  {selectedBaseIngredientsOnly.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Ingredientes
                      </span>
                      <ul className="list-disc ml-5 mt-2 text-sm text-gray-700 space-y-1">
                        {selectedBaseIngredientsOnly.map((ing, idx) => (
                          <li key={idx} className="mb-0">
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-gray-900">
                                {ing.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {ing.qty}x
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Extra Ingredientes selecionados */}
                  {selectedExtraIngredients.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Extra Ingredientes
                      </span>
                      <ul className="list-disc ml-5 mt-2 text-sm text-gray-700 space-y-1">
                        {selectedExtraIngredients.map((ing, idx) => (
                          <li key={idx} className="mb-0">
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-gray-900">
                                {ing.name}
                              </span>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>x{ing.qty}</span>
                                {ing.price ? (
                                  <span>
                                    {formatBRL(ing.price * (ing.qty || 1))}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {item.comments != "" && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <i>Observação: </i>
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {item.comments}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      className="p-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200"
                      onClick={() => updateCartQuantity(item.id, quantity - 1)}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-6 text-center text-sm">{quantity}</span>
                    <button
                      className="p-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200"
                      onClick={() => updateCartQuantity(item.id, quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      className="ml-2 p-1 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-6 border-t bg-gray-50 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-gray-700">
            Total a pagar
          </span>
          <span className="text-2xl font-bold text-gray-900">
            {formatBRL(totalPrice)}
          </span>
        </div>
        <button
          type="button"
          disabled={loja === false || isLoadingNext}
          onClick={handleNext}
          className={
            loja === true
              ? "w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3 px-6 rounded-xl font-bold text-lg transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-wait flex items-center justify-center gap-2"
              : "w-full bg-gray-300 text-gray-600 py-3 px-6 rounded-xl font-bold text-lg cursor-not-allowed"
          }
        >
          {(() => {
            if (isLoadingNext) {
              return (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Carregando...</span>
                </>
              );
            }
            if (loja === true) {
              return "Ir para Entrega e Pagamento";
            }
            return "Loja Fechada no momento";
          })()}
        </button>
      </div>
    </div>
  );
}

/* ====== Etapa 2 – Entrega + formulário (mantido) ====== */
function Step2Delivery({
  deliveryType,
  setDeliveryType,
  validarDelivery,
  showMessage,
  customerInfo,
  setCustomerInfo,
  address,
  setAddress,
  maskCEP,
  onlyDigits,
  nameInvalid,
  phoneInvalid,
  cepInvalid,
  logradouroInvalid,
  bairroInvalid,
  ufInvalid,
  numeroInvalid,
  subtotalPrice,
  freight,
  freightLoading,
  calculateFreight,
  clearFreight,
  totalPrice,
  isFormValid,
  onBack,
  onNext,
}) {
  const isDelivery = deliveryType === "delivery";
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [loadingDeliveryValidation, setLoadingDeliveryValidation] =
    useState(false);
  const [isComponentReady, setIsComponentReady] = useState(false);

  // Garante que o componente está montado antes de renderizar
  useEffect(() => {
    setIsComponentReady(true);
  }, []);

  // Limpa frete quando mudar para retirada
  useEffect(() => {
    if (!isDelivery) {
      clearFreight();
    }
  }, [isDelivery, clearFreight]);

  // Calcula frete automaticamente quando CEP for preenchido
  useEffect(() => {
    if (!isComponentReady) return;

    const cepDigits = onlyDigits(address.cep);

    if (isDelivery && cepDigits.length === 8 && address.logradouro) {
      // Só calcula se ainda não tiver frete calculado para este CEP
      if (!freight || freight.destinationCep !== cepDigits) {
        const timer = setTimeout(async () => {
          try {
            const freightResult = await calculateFreight(cepDigits);
            if (freightResult) {
              if (freightResult.available) {
                showMessage(
                  "Frete Calculado",
                  `${
                    freightResult.message
                  }\nDistância: ${freightResult.distanceKm.toFixed(
                    2
                  )} km\nValor: ${formatBRL(freightResult.value)}`,
                  "success"
                );
              } else {
                showMessage(
                  "Frete Indisponível",
                  freightResult.message,
                  "warning"
                );
              }
            }
          } catch (error) {
            console.error("Erro ao calcular frete:", error);
          }
        }, 500); // Debounce de 500ms

        return () => clearTimeout(timer);
      }
    }
  }, [
    address.cep,
    address.logradouro,
    isDelivery,
    calculateFreight,
    freight,
    onlyDigits,
    showMessage,
    isComponentReady,
  ]);

  // Exibe loading enquanto componente não estiver pronto
  if (!isComponentReady) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-10 w-10 text-brand-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600 text-sm">Carregando informações...</p>
        </div>
      </div>
    );
  }

  // Função para buscar CEP via ViaCEP
  const handleBuscarCEP = async () => {
    const cepDigits = onlyDigits(address.cep);

    if (cepDigits.length !== 8) {
      showMessage(
        "CEP Inválido",
        "Por favor, informe um CEP válido com 8 dígitos.",
        "warning"
      );
      return;
    }

    try {
      setLoadingCEP(true);
      const response = await fetch(
        `https://viacep.com.br/ws/${cepDigits}/json/`
      );

      if (!response.ok) {
        throw new Error("Erro ao consultar CEP");
      }

      const data = await response.json();

      if (data.erro) {
        showMessage(
          "CEP não encontrado",
          "CEP não encontrado. Verifique e tente novamente.",
          "warning"
        );
        setAddress((prev) => ({
          ...prev,
          logradouro: "",
          bairro: "",
          uf: "",
          estado: "",
        }));
        return;
      }

      // Mapa de UFs para nomes de estados
      const UF_NAMES = {
        AC: "Acre",
        AL: "Alagoas",
        AP: "Amapá",
        AM: "Amazonas",
        BA: "Bahia",
        CE: "Ceará",
        DF: "Distrito Federal",
        ES: "Espírito Santo",
        GO: "Goiás",
        MA: "Maranhão",
        MT: "Mato Grosso",
        MS: "Mato Grosso do Sul",
        MG: "Minas Gerais",
        PA: "Pará",
        PB: "Paraíba",
        PR: "Paraná",
        PE: "Pernambuco",
        PI: "Piauí",
        RJ: "Rio de Janeiro",
        RN: "Rio Grande do Norte",
        RS: "Rio Grande do Sul",
        RO: "Rondônia",
        RR: "Roraima",
        SC: "Santa Catarina",
        SP: "São Paulo",
        SE: "Sergipe",
        TO: "Tocantins",
      };

      const uf = data.uf || "";
      setAddress((prev) => ({
        ...prev,
        logradouro: data.logradouro || "",
        bairro: data.bairro || "",
        uf,
        localidade: data.localidade || "",
        estado: UF_NAMES[uf] || data.localidade || "",
      }));

      // Após buscar CEP com sucesso, calcula frete automaticamente
      if (isDelivery) {
        try {
          const freightResult = await calculateFreight(cepDigits);
          if (freightResult) {
            if (freightResult.available) {
              showMessage(
                "Frete Calculado",
                `${
                  freightResult.message
                }\nDistância: ${freightResult.distanceKm.toFixed(
                  2
                )} km\nValor: ${formatBRL(freightResult.value)}`,
                "success"
              );
            } else {
              showMessage(
                "Frete Indisponível",
                freightResult.message,
                "warning"
              );
            }
          }
        } catch (freightError) {
          console.error("Erro ao calcular frete:", freightError);
          showMessage(
            "Erro ao Calcular Frete",
            "Não foi possível calcular o frete. Tente novamente.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      showMessage(
        "Erro ao Buscar CEP",
        "Não foi possível consultar o CEP agora. Tente novamente.",
        "error"
      );
    } finally {
      setLoadingCEP(false);
    }
  };

  return (
    <>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setDeliveryType("pickup")}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${
                deliveryType === "pickup"
                  ? "bg-brand-primary text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              📍 Retirada
            </button>
            <button
              onClick={async () => {
                setLoadingDeliveryValidation(true);
                try {
                  const result = await validarDelivery();
                  if (result) {
                    // Apenas mostra mensagem se delivery estiver indisponível
                    if (!result.success) {
                      showMessage(
                        "Delivery Temporariamente Indisponível",
                        result.message,
                        "warning"
                      );
                    }
                    // Se disponível, apenas seleciona delivery sem mostrar mensagem
                    // As mensagens de frete (disponível/indisponível) serão mostradas após calcular
                  }
                } catch (error) {
                  console.error("Erro ao validar delivery:", error);
                  showMessage(
                    "Erro ao Validar Delivery",
                    "Não foi possível validar o delivery. Tente novamente.",
                    "error"
                  );
                } finally {
                  setLoadingDeliveryValidation(false);
                }
              }}
              disabled={loadingDeliveryValidation}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
                deliveryType === "delivery"
                  ? "bg-brand-primary text-white"
                  : "bg-gray-100 text-gray-700"
              } ${loadingDeliveryValidation ? "opacity-70 cursor-wait" : ""}`}
            >
              {loadingDeliveryValidation ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Validando...</span>
                </>
              ) : (
                "🚚 Delivery"
              )}
            </button>
          </div>
          <div className="text-right text-sm">
            <span className="text-gray-500 block">Subtotal</span>
            <span className="font-bold text-gray-800">
              {formatBRL(subtotalPrice)}
            </span>
            {isDelivery && freight?.available && (
              <>
                <span className="text-gray-500 block text-xs mt-1">
                  + Frete
                </span>
                <span className="font-semibold text-emerald-600 text-sm">
                  {formatBRL(freight.value)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Dados básicos */}
        <div className="grid grid-cols-1 gap-4 mb-2">
          <input
            type="text"
            placeholder="Seu nome"
            value={customerInfo.name}
            onChange={(e) =>
              setCustomerInfo((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            style={{
              borderColor: nameInvalid ? "rgb(var(--color-brand-600))" : undefined,
            }}
          />
          <input
            type="tel"
            placeholder="(00) 00000-0000"
            value={customerInfo.phone}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "");
              let masked = digits;

              if (digits.length <= 11) {
                // Aplica máscara: (00) 00000-0000
                if (digits.length > 10) {
                  masked = digits.replace(
                    /^(\d{2})(\d{5})(\d{0,4}).*/,
                    "($1) $2-$3"
                  );
                } else if (digits.length > 6) {
                  masked = digits.replace(
                    /^(\d{2})(\d{4})(\d{0,4}).*/,
                    "($1) $2-$3"
                  );
                } else if (digits.length > 2) {
                  masked = digits.replace(/^(\d{2})(\d{0,5})/, "($1) $2");
                } else if (digits.length > 0) {
                  masked = digits.replace(/^(\d*)/, "($1");
                }
              }

              setCustomerInfo((prev) => ({
                ...prev,
                phone: masked,
              }));
            }}
            className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            style={{
              borderColor: phoneInvalid ? "rgb(var(--color-brand-600))" : undefined,
            }}
            maxLength={15}
          />
        </div>

        {/* Endereço para delivery */}
        {isDelivery && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <input
                type="text"
                placeholder="CEP"
                value={address.cep}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    cep: maskCEP(e.target.value),
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                style={{
                  borderColor: cepInvalid ? "rgb(var(--color-brand-600))" : undefined,
                }}
              />
              <button
                type="button"
                onClick={handleBuscarCEP}
                disabled={loadingCEP || freightLoading}
                className="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm hover:bg-gray-800 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Truck className="w-4 h-4" />
                {loadingCEP || freightLoading
                  ? "Calculando..."
                  : "Calcular frete"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-2">
              <input
                type="text"
                placeholder="Endereço"
                value={address.logradouro}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    logradouro: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent col-span-2"
                style={{
                  borderColor: logradouroInvalid ? "rgb(var(--color-brand-600))" : undefined,
                }}
              />
              <input
                type="text"
                placeholder="Número"
                value={address.numero}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    numero: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                style={{
                  borderColor: numeroInvalid ? "rgb(var(--color-brand-600))" : undefined,
                }}
              />
              <input
                type="text"
                placeholder="Complemento (opcional)"
                value={address.complemento}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    complemento: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Bairro"
                value={address.bairro}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    bairro: e.target.value,
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent col-span-2"
                style={{
                  borderColor: bairroInvalid ? "rgb(var(--color-brand-600))" : undefined,
                }}
              />
              <input
                type="text"
                placeholder="UF"
                value={address.uf}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    uf: e.target.value.toUpperCase().slice(0, 2),
                  }))
                }
                className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                style={{
                  borderColor: ufInvalid ? "rgb(var(--color-brand-600))" : undefined,
                }}
              />
              <input
                type="text"
                placeholder="Cidade"
                value={address.estado}
                readOnly
                className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50"
              />
            </div>
          </>
        )}

        {/* Informações de frete para Delivery */}
        {isDelivery && freight && (
          <div
            className={`rounded-xl p-4 border-2 ${
              freight.available
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {freight.available ? (
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-emerald-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <X className="w-5 h-5 text-amber-600" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h4
                  className={`font-semibold text-sm mb-1 ${
                    freight.available ? "text-emerald-900" : "text-amber-900"
                  }`}
                >
                  {freight.available
                    ? "Frete Disponível"
                    : "Frete Indisponível"}
                </h4>
                <p
                  className={`text-sm ${
                    freight.available ? "text-emerald-700" : "text-amber-700"
                  }`}
                >
                  {freight.message}
                </p>
                {freight.available && (
                  <div className="mt-2 flex items-center gap-4 text-xs text-emerald-600">
                    <span>
                      📍 Distância: {freight.distanceKm.toFixed(2)} km
                    </span>
                    <span className="font-bold">
                      💰 {formatBRL(freight.value)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="p-6 border-t bg-gray-50 flex flex-col gap-3">
        {isDelivery && freight?.available && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal dos produtos:</span>
              <span className="font-semibold">{formatBRL(subtotalPrice)}</span>
            </div>
            <div className="flex justify-between text-emerald-700">
              <span>Frete:</span>
              <span className="font-semibold">{formatBRL(freight.value)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
              <span>Total:</span>
              <span>{formatBRL(totalPrice)}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onBack}
            className="w-full sm:w-auto px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={
              !isFormValid ||
              (isDelivery && freightLoading) ||
              (isDelivery && !freight)
            }
            className="w-full sm:w-auto px-4 py-2 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {freightLoading
              ? "Calculando frete..."
              : isDelivery && !freight
              ? "Calcule o frete para continuar"
              : "Ir para pagamento"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ====== Etapa 3 – Pagamento ====== */
function Step3Payment({
  payments,
  paymentMethod,
  setPaymentMethod,
  paymentMethodSelected,
  setPaymentMethodSelected,
  subtotalPrice,
  freight,
  deliveryType,
  totalPrice,
  discountValue = 0,
  couponLoading = false,
  couponError = null,
  couponData = null,
  validateCoupon = () => {},
  clearCoupon = () => {},
  onBack,
  onFinalizeOrder,
  isFinalizing = false,
}) {
  const isDelivery = deliveryType === "delivery";

  return (
    <>
      <div className="p-6 space-y-4">
        {/* Seção de Cupom de Desconto */}
        <div>
          <h3 className="font-bold text-gray-800 mb-2">Cupom de Desconto</h3>
          <CouponInput
            onValidate={validateCoupon}
            onClear={clearCoupon}
            loading={couponLoading}
            error={couponError}
            couponData={couponData}
            totalValue={subtotalPrice}
            discountValue={discountValue}
          />
        </div>

        <div>
          <h3 className="font-bold text-gray-800 mb-2">
            Como você quer pagar?
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {payments.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setPaymentMethod(p.id);
                  setPaymentMethodSelected(p.name || p.description || "");
                }}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm flex items-center justify-between ${
                  paymentMethod === p.id
                    ? "border-brand-primary bg-red-50"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <span>{p.name || p.description}</span>
                {paymentMethod === p.id && (
                  <span className="text-xs font-semibold text-brand-primary">
                    Selecionado
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 bg-gray-50 rounded-xl p-4 space-y-2">
          <h4 className="font-semibold text-gray-700 mb-3">Resumo do Pedido</h4>

          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal dos produtos:</span>
            <span className="font-semibold">{formatBRL(subtotalPrice)}</span>
          </div>

          {isDelivery && freight?.available && (
            <div className="flex justify-between text-sm text-emerald-700">
              <span className="flex items-center gap-1">
                <Truck className="w-4 h-4" />
                Frete:
              </span>
              <span className="font-semibold">{formatBRL(freight.value)}</span>
            </div>
          )}

          {discountValue > 0 && (
            <div className="flex justify-between text-sm text-green-700">
              <span className="flex items-center gap-1">
                <Tag className="w-4 h-4" />
                Desconto (Cupom):
              </span>
              <span className="font-semibold">-{formatBRL(discountValue)}</span>
            </div>
          )}

          <div className="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t border-gray-200">
            <span>Total a pagar:</span>
            <span className="text-brand-primary">{formatBRL(totalPrice)}</span>
          </div>
        </div>
      </div>

      <div className="p-6 border-t bg-gray-50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={onFinalizeOrder}
          disabled={!paymentMethod || isFinalizing}
          className="w-full sm:w-auto px-4 py-2 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isFinalizing ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Finalizando...</span>
            </>
          ) : (
            "Finalizar Pedido"
          )}
        </button>
      </div>
    </>
  );
}
